import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';
import { useStore } from 'effector-react';
import { closeModal } from '@stores/modals';
import { DatePicker, Form, Input, Modal, Radio, Spin, Typography } from 'antd';
import { ALL_LOCALE, LOCALE_NAME } from '@stores/localeWeight';
import GameEditModalPlatforms from '@components/GameEditModal/GameEditModalPlatforms';
import postSelector, { getPostById, reset } from '@stores/postSelector';
import FeaturingPostPreview from '../FeaturingPostPreview/FeaturingPostPreview';
import DebounceSelect from '@fields/DebounceSelect/DebounceSelect';
import {
  fetchGameList,
  mapDataToFieldsValue,
  mapValueToRequest,
} from './FeaturingEditModal.helpers';
import { FEATURING_TYPE, FeaturingFormValue } from './FeaturingEditModal.types';
import featuring, { createFeaturing, editFeaturing } from '@stores/featuring';
import { FULL_TIME_FORMAT, TIME_FORMAT } from '@config/format';
import { IFeaturing } from '@interfaces/IFeaturing';
import useTranslate from '@hooks/useTranslate';
import componentContent from '../FeaturingPage.module.json';

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};

const LOCALES = ALL_LOCALE.map((locale) => ({
  label: LOCALE_NAME[locale],
  value: locale,
}));

const FeaturingEditModal: FunctionComponent<{ data?: IFeaturing }> = ({ data }) => {
  const content = useTranslate(componentContent);
  const [form] = Form.useForm();
  const {
    post,
    preloader: { getPostById: preloaderPost },
  } = useStore(postSelector);
  const locales = useMemo(() => [{ label: 'All', value: 'all' }, ...LOCALES], []);
  const { preloaders } = useStore(featuring);

  useEffect(() => {
    if (data) {
      const fieldsValue = mapDataToFieldsValue(data);
      form.setFieldsValue(fieldsValue);
      getPostById(fieldsValue.postId);
    } else {
      form.resetFields();
      reset();
    }
  }, [data, form]);

  const ok = useCallback(() => {
    form.submit();
  }, [form]);

  const submit = useCallback((value: FeaturingFormValue) => {
    const { body, id } = mapValueToRequest(value);
    if (id) {
      editFeaturing({ data: body, id });
    } else {
      createFeaturing(body);
    }
  }, []);

  useEffect(() => {
    if (!post.isValid) {
      form.setFields([
        {
          name: 'postId',
          errors: [content.valid_post],
        },
      ]);
    }
  }, [content.valid_post, form, post.isValid]);

  return (
    <Modal
      title={data ? content.edit_featuring : content.add_featuring}
      visible
      onCancel={() => closeModal({ name: 'featuring' })}
      onOk={ok}
      width={800}
    >
      <Form
        onFinish={(value) => submit(value)}
        form={form}
        {...layout}
        initialValues={{
          type: FEATURING_TYPE.STAGE_1,
          locale: 'all',
        }}
      >
        <Spin spinning={!!preloaders.createFeaturing || !!preloaders.editFeaturing}>
          {data ? (
            <Form.Item label='id' name='id'>
              <Typography.Text type='secondary'>{data.id}</Typography.Text>
            </Form.Item>
          ) : null}
          <Form.Item
            label={content.post_id}
            name='postId'
            validateTrigger='onBlur'
            rules={[
              {
                required: true,
              },
              {
                validator: (rule, value) => {
                  if (value) {
                    return getPostById(value)
                      .then(() => {
                        return Promise.resolve();
                      })
                      .catch(() => {
                        return Promise.reject(content.valid_post);
                      });
                  } else {
                    reset();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input type={'number'} />
          </Form.Item>
          {
            <Spin spinning={!!preloaderPost}>
              {post.data ? <FeaturingPostPreview post={post.data} /> : null}
            </Spin>
          }
          <Form.Item label={content.featuring_level} name='type'>
            <Radio.Group>
              <Radio value={FEATURING_TYPE.STAGE_1}>1</Radio>
              <Radio value={FEATURING_TYPE.STAGE_2}>2</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={content.display_period}
            name='date'
            rules={[
              {
                required: true,
              },
            ]}
          >
            <DatePicker.RangePicker showTime={{ format: TIME_FORMAT }} format={FULL_TIME_FORMAT} />
          </Form.Item>
          <Form.Item label={content.locales} name='locale'>
            <Radio.Group>
              {locales.map((locale) => (
                <Radio value={locale.value} key={locale.value}>
                  {locale.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
          <Form.Item label={content.games} name='gameIds'>
            <DebounceSelect
              mode='multiple'
              placeholder={content.select_games}
              fetchOptions={fetchGameList}
            />
          </Form.Item>
          <Form.Item label={content.platforms} name='platformIds'>
            <GameEditModalPlatforms />
          </Form.Item>
        </Spin>
      </Form>
    </Modal>
  );
};

export default FeaturingEditModal;
